import { countActive, DEFAULT_FILTERS, type IActivityFilters } from './activitiesFilter.js';

/**
 * The page's filters: the shared set, plus the panel's Status checkboxes as
 * the statuses the page HIDES. One set for every view — each applies the
 * statuses it offers (the Daily List and the Work Plan an activity's rung and
 * an issue's keyed status, the Progress Tracker the plan statuses; the mobile
 * list an activity's rung). The shared single `status` is not used here.
 */
export interface IPageFilters extends IActivityFilters {
  hiddenStatuses: string[];
}

/** An issue's status as the Status checkboxes key it — apart from an
 *  activity's, so an issue's `planned` ("Open") is its own box. */
export const issueStatusKey = (status: string): string => `issue:${status}`;

/** An issue's statuses, in the Issues page's order. */
export const ISSUE_STATUSES = ['planned', 'forReview', 'rejected', 'completed'] as const;
export const ISSUE_STATUS_KEYS: readonly string[] = ISSUE_STATUSES.map(issueStatusKey);

/** Out of the box every status shows but done — an activity's, and an
 *  issue's (completed). */
export const DEFAULT_HIDDEN_STATUSES: readonly string[] = ['done', issueStatusKey('completed')];

export const DEFAULT_PAGE_FILTERS: IPageFilters = { ...DEFAULT_FILTERS, hiddenStatuses: [...DEFAULT_HIDDEN_STATUSES] };

/** Whether a status's checkbox departs from its default (the badges count these). */
export function isStatusChanged(hidden: readonly string[], status: string): boolean {
  return hidden.includes(status) !== DEFAULT_HIDDEN_STATUSES.includes(status);
}

/** The filter button's count: the shared filters, plus one when the Status
 *  checkboxes differ from their default. */
export function countPageFilters(filters: IPageFilters): number {
  const statusesChanged = [...new Set([...filters.hiddenStatuses, ...DEFAULT_HIDDEN_STATUSES])].some((status) =>
    isStatusChanged(filters.hiddenStatuses, status),
  );
  return countActive(filters) + (statusesChanged ? 1 : 0);
}
